"use client";

import { useState } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import type { HeatmapTile } from "@/lib/screener/index-membership";
import { PRICE_RETURN_PERIODS, type PriceReturnPeriodKey } from "@/lib/data/price-return";

type ColorMode = "score" | "performance";

/** Saettigungs-Schwelle je Zeitraum fuer performanceColor() - kuerzere Zeitraeume haben
 * naturgemaess kleinere typische Kursausschlaege, ein fixes ±30%-Fenster wie fuer 1J
 * wuerde z.B. Tagesperformance-Kacheln fast immer blass/kaum unterscheidbar erscheinen
 * lassen. Grob an typischer Volatilitaet je Fenster orientiert, keine statistische
 * Herleitung noetig - rein visuelle Kalibrierung. */
const PERFORMANCE_CLAMP: Record<PriceReturnPeriodKey, number> = {
  "1t": 0.03,
  "1w": 0.07,
  "1m": 0.15,
  "3m": 0.2,
  "6m": 0.25,
  ytd: 0.3,
  "1y": 0.3,
  "3y": 0.6,
  "5y": 1,
};

/** Rot -> Gold -> Grün ueber 0-100, dieselben Projektfarben wie der Finanzielle-
 * Gesundheit-Tacho (components/financial-health-gauge.tsx) - zweistufige lineare
 * Interpolation zwischen den drei Referenzpunkten. */
function scoreColor(score: number): string {
  const stops: [number, [number, number, number]][] = [
    [0, [180, 84, 76]], // --negative #b4544c
    [50, [196, 161, 90]], // --gold #c4a15a
    [100, [63, 122, 91]], // --positive #3f7a5b
  ];
  const [loStop, hiStop] = score <= 50 ? [stops[0], stops[1]] : [stops[1], stops[2]];
  const t = (score - loStop[0]) / (hiStop[0] - loStop[0]);
  const [r, g, b] = loStop[1].map((c, i) => Math.round(c + (hiStop[1][i] - c) * t));
  return `rgb(${r}, ${g}, ${b})`;
}

/** Gleiches Farbschema wie scoreColor(), aber um 0 % zentriert statt um Score 50 - ab
 * `clamp` Kursveränderung erreicht die Farbe bereits die volle Sättigung (jenseits davon
 * geklemmt), da eine lineare Skala bis z.B. 100 % die meisten Kacheln optisch "verflachen"
 * würde. `clamp` ist zeitraumabhängig (PERFORMANCE_CLAMP unten) - ein fixes Fenster würde
 * z.B. Tagesperformance-Kacheln fast immer blass erscheinen lassen. */
function performanceColor(returnPct: number, clamp: number): string {
  const clamped = Math.max(-clamp, Math.min(clamp, returnPct));
  return scoreColor(50 + (clamped / clamp) * 50);
}

/**
 * Sektor-/Index-Heatmap (backlog.md -> Punkt 8, Zeitraum-Umschalter): farbcodierte
 * Kachelübersicht, wahlweise nach Erikonomie-Score oder Kursperformance über einen von 9
 * wählbaren Zeiträumen (1 Tag bis 5 Jahre, PRICE_RETURN_PERIODS) - alle Metriken kommen
 * bereits aus dem gecachten ScoreSummary (kein Zusatz-Request, priceReturns wird aus der
 * ohnehin für computeScore() geladenen Kurshistorie abgeleitet, siehe
 * lib/data/price-return.ts). Symbole ohne Daten im gecachten Universum
 * werden bewusst NEUTRAL/GRAU dargestellt statt rot - eine rote Kachel würde fälschlich
 * "schlechte Aktie" statt "keine Daten verfügbar" suggerieren (Datenakkuratesse-Risiko,
 * das nur Heatmaps haben, Tabellen nicht). Einfaches CSS-Grid statt einer
 * Treemap-Bibliothek.
 */
export function IndexHeatmapGrid({ tiles }: { tiles: HeatmapTile[] }) {
  const [mode, setMode] = useState<ColorMode>("score");
  const [period, setPeriod] = useState<PriceReturnPeriodKey>("1y");

  if (tiles.length === 0) {
    return <p className="text-sm text-muted-foreground">Für diese Auswahl liegen aktuell keine Daten vor.</p>;
  }

  const valueOf = (tile: HeatmapTile) => (mode === "score" ? tile.score : tile.priceReturns?.[period]);
  const colorOf = (value: number) => (mode === "score" ? scoreColor(value) : performanceColor(value, PERFORMANCE_CLAMP[period]));
  const labelOf = (tile: HeatmapTile) => {
    const value = valueOf(tile);
    if (value === undefined) return "k. A.";
    return mode === "score" ? String(Math.round(value)) : `${value >= 0 ? "+" : ""}${Math.round(value * 100)} %`;
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex gap-1 rounded-full bg-muted p-1">
          {(
            [
              ["score", "Score"],
              ["performance", "Performance"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setMode(key)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                mode === key ? "bg-card text-ink shadow-sm" : "text-muted-foreground hover:text-ink",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {mode === "performance" && (
          <div className="inline-flex gap-1 rounded-full bg-muted p-1">
            {PRICE_RETURN_PERIODS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setPeriod(key)}
                className={cn(
                  "rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                  period === key ? "bg-card text-ink shadow-sm" : "text-muted-foreground hover:text-ink",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 md:grid-cols-8">
        {tiles.map((tile) => {
          const value = valueOf(tile);
          return (
            <Link
              key={tile.symbol}
              href={`/${encodeURIComponent(tile.symbol)}`}
              className="flex aspect-square flex-col items-center justify-center gap-1 rounded-md p-2 text-center transition-opacity hover:opacity-90"
              style={{ backgroundColor: value !== undefined ? colorOf(value) : "var(--muted)" }}
              title={tile.name}
            >
              <span
                className="line-clamp-2 text-[0.65rem] font-medium leading-tight"
                style={{ color: value !== undefined ? "#FAF7F0" : "var(--muted-foreground)" }}
              >
                {tile.symbol}
              </span>
              <span
                className="font-tabular text-sm font-semibold"
                style={{ color: value !== undefined ? "#FAF7F0" : "var(--muted-foreground)" }}
              >
                {labelOf(tile)}
              </span>
            </Link>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "rgb(180, 84, 76)" }} />
          {mode === "score" ? "Niedriger Score" : "Kursverlust"}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "rgb(196, 161, 90)" }} />
          {mode === "score" ? "Mittlerer Score" : "Kaum verändert"}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "rgb(63, 122, 91)" }} />
          {mode === "score" ? "Hoher Score" : "Kursgewinn"}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-muted" />
          Keine Daten verfügbar
        </span>
      </div>
    </div>
  );
}
